import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import {
  array,
  boolean,
  check,
  fallback,
  looseObject,
  nullable,
  object,
  optional,
  pipe,
  safeParse,
  string,
  union,
} from "valibot";
import { dataName, errorTranslation, gridFSFile, integerValue, label, photoQuality } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { EXISTS_CONDITION_KEYS, MAX_CHARACTERS_XL, MAX_PHOTOS } from "../../constants";
import { useFormServices } from "../../services/formServices";
import MixedInput from "../../components/inputs/MixedInput";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { SEARCHABLE_FIELDS } from "../../utils/fieldUtil";
import {
  parseStringTemplate,
  parseStringTemplateForField,
  stringTemplateToString,
} from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { SEARCHABLE_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import Image from "../valueRendering/Image";

import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { SingleSelectInput } from "../../components/menu/Select";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import { Toggle } from "../../components/toggle/Toggle";
import PropertySection from "../PropertySection";
import { keyFromSelector } from "i18next";
import { NumberInput } from "../../components/inputs/NumberInput.tsx";
import { isNil } from "lodash-es";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage.tsx";
import { createEffect, createMemo, For, Show } from "solid-js";
import FieldValueReference from "../FieldValueReference.ts";
import { getDynamicValueIcon } from "../../utils/menu/optionIconUtil.ts";
import DetailModal from "../../components/modal/DetailModal.tsx";
import { useModalManager } from "../../components/modal/ModalManager.tsx";
import InlineBanner from "../../components/banner/InlineBanner.tsx";
import { WarningModal } from "../../components/modal/Modal.tsx";
import isFeatureEnabled from "../../utils/featureFlag.ts";
import FeatureFlag from "../../types/FeatureFlag.ts";

const DtoSchema = looseObject({
  label_text: string(),
  data_name: string(),
  quality: photoQuality(),
  filename: string(),
  allow_library: boolean(),
  save_to_gallery: boolean(),
  required: boolean(),
  min_photos: integerValue(0, MAX_PHOTOS),
  max_photos: nullable(integerValue(1, MAX_PHOTOS)),
  isMultiple: optional(boolean()),
});

const DomainSchema = pipe(
  object({
    label_text: label(),
    data_name: dataName(),
    quality: fallback(photoQuality(), "high"),
    filename: stringTemplate(SEARCHABLE_FIELDS, SEARCHABLE_VARIABLES, [], MAX_CHARACTERS_XL, false),
    allow_library: fallback(boolean(), true),
    save_to_gallery: fallback(boolean(), false),
    required: fallback(boolean(), false),
    min_photos: fallback(integerValue(0, MAX_PHOTOS), 0),
    max_photos: fallback(nullable(integerValue(1, MAX_PHOTOS)), 1),
    isMultiple: fallback(boolean(), false),
  }),
  check(
    ({ min_photos, max_photos }) => isNil(max_photos) || max_photos >= (min_photos ?? 0),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMERIC_MAX_GREATER_EQUALS_MIN)),
  ),
);

const ValueSchema = union([gridFSFile(), array(gridFSFile())]);

const isMultiplePhotos = (value: string | string[]): value is string[] => Array.isArray(value);

export type PhotoFieldProperties = InferOutput<typeof DomainSchema>;
export type PhotoFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const PhotoField: Field<PhotoFieldProperties, PhotoFieldDtoType, ValueType> = {
  type: "photo",
  category: "media",
  widget: "com.moreapps:photo:1",
  schema: DomainSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    quality: "high",
    allow_library: true,
    save_to_gallery: false,
    required: false,
    filename: [],
    min_photos: 0,
    max_photos: 1,
    isMultiple: true,
  }),
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    filename: properties.filename.map(updateReference),
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => (isMultiplePhotos(value) ? value.join(", ") : value),
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();
    const { open } = useModalManager();
    const photoArray = Array.isArray(value) ? value : [value];

    const openModal = () =>
      open({
        content: () => (
          <DetailModal
            title={fieldConfig.properties.label_text}
            iconTile={{
              icon: PhotoField.icon,
              category: PhotoField.category,
            }}
            content={() => (
              <div
                classList={{
                  "grid gap-2": true,
                  [photoArray.length <= 4 ? "grid-cols-2" : "grid-cols-3"]: true,
                }}
              >
                <For each={photoArray}>
                  {(item) => (
                    <Image
                      fileId={item}
                      scope="submission"
                      ariaLabel={t(PhotoField.titleKey)}
                      modalTitle={fieldConfig.properties.label_text}
                      modalIcon={PhotoField.icon}
                      modalCategory={PhotoField.category}
                      cover
                    />
                  )}
                </For>
              </div>
            )}
          />
        ),
      });

    return (
      <div class="relative">
        <Show when={isMultiplePhotos(value) && value.length > 1}>
          <button
            type="button"
            class="absolute flex size-full h-20 w-full items-center justify-center rounded-sm border border-subtle bg-overlay-harsh hover:bg-overlay-harsh-hover"
            onClick={openModal}
            aria-label={t(($) => $.FIELD_PROPERTY_OPEN_MULTIPLE_PHOTOS, { amount: value.length - 1 })}
          >
            <p aria-hidden="true" class="text-header-xl text-inverse">
              +{value.length - 1}
            </p>
          </button>
        </Show>
        <Show when={photoArray[0]}>
          {(fileId) => (
            <Image
              fileId={fileId()}
              scope="submission"
              ariaLabel={t(PhotoField.titleKey)}
              modalTitle={fieldConfig.properties.label_text}
              modalIcon={PhotoField.icon}
              modalCategory={PhotoField.category}
            />
          )}
        </Show>
      </div>
    );
  },
  icon: "camera",
  titleKey: keyFromSelector(($) => $.PHOTO_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.PHOTO_FIELD_DESCRIPTION),
  propertyKeys: {
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    quality: keyFromSelector(($) => $.FIELD_PROPERTY_PHOTO_QUALITY),
    filename: keyFromSelector(($) => $.FIELD_PROPERTY_FILE_NAME),
    min_photos: keyFromSelector(($) => $.FIELD_PROPERTY_MIN_PHOTOS),
    max_photos: keyFromSelector(($) => $.FIELD_PROPERTY_MAX_PHOTOS),
    allow_library: keyFromSelector(($) => $.FIELD_PROPERTY_ALLOW_LIBRARY),
    save_to_gallery: keyFromSelector(($) => $.FIELD_PROPERTY_SAVE_TO_GALLERY),
    isMultiple: keyFromSelector(($) => $.FIELD_PROPERTY_MULTIPLE_PHOTOS),
  },
  toDto: (domain, fields, variables) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      filename: stringTemplateToString(domain.filename, fields, variables),
    });
    return output as PhotoFieldDtoType;
  },
  postProcessFromDto: (domain, fields, variables, scope) => ({
    ...domain,
    filename: parseStringTemplateForField(
      domain.filename,
      fields.filter((field) => field.scope === scope),
      variables,
    ),
  }),
  valueReferenceOptions: (id, props) => {
    const referenceId = FieldValueReference.from(id.toString(), "image");
    const icon = getDynamicValueIcon(referenceId, "photo");
    return [
      {
        variant: "dynamicValue",
        value: referenceId,
        label: props.label_text,
        pill: props.data_name,
        icon,
      },
    ];
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <div class="flex w-full flex-col gap-y-4">
          <FieldButtonPreview
            icon="camera"
            iconType="outline"
            text={t(($) => $.PHOTO_FIELD_PREVIEW_ADD_BUTTON_LABEL)}
          />
          <FieldButtonPreview
            icon="photograph"
            iconType="outline"
            text={t(($) => $.PHOTO_FIELD_PREVIEW_ADD_MULTIPLE_BUTTON_LABEL)}
          />
        </div>
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();
    const globalErrors = createMemo(() => props.getGlobalErrors());
    const { open } = useModalManager();

    createEffect(() => {
      if (!isNil(props.data.properties.min_photos) && props.data.properties.min_photos >= 1) {
        fieldService.setFieldProperty(props.uid, "required", true);
      } else {
        fieldService.setFieldProperty(props.uid, "required", false);
      }
    });

    // TODO: DEV-8358 - Can be removed once all photo widgets send data as a list
    const openUpgradeModal = () =>
      open({
        content: () => (
          <WarningModal
            title={t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_MODAL_TITLE)}
            description={t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_MODAL_DESCRIPTION)}
            confirmLabel={t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_MODAL_CONFIRM_BUTTON)}
            cancelLabel={t(($) => $.CANCEL_BUTTON)}
            onConfirm={() => props.setValue("isMultiple", true)}
          />
        ),
      });

    return (
      <>
        {/* TODO: DEV-8358 - Can be removed once all photo widgets send data as a list */}
        <Show when={isFeatureEnabled(FeatureFlag.MULTIPLE_PHOTOS) && !props.data.properties.isMultiple}>
          <div class="mb-6 [&>div]:w-full">
            <InlineBanner
              status="upgrade"
              title={t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_BANNER_TITLE)}
              description={t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_BANNER_DESCRIPTION)}
              secondaryAction={{
                label: t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_BANNER_SECONDARY_BUTTON),
                onClick: () => window.open("https://links.moreapp.com/help-center"),
              }}
              primaryAction={{
                label: t(($) => $.PHOTO_FIELD_MULTIPLE_UPGRADE_BANNER_PRIMARY_BUTTON),
                onClick: openUpgradeModal,
              }}
            />
          </div>
        </Show>
        <TextInput
          label={t(props.propertyKeys.label_text)}
          value={props.data.properties.label_text}
          onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
          errorMessage={props.getError("label_text")}
          required
        />

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_PHOTO)}>
          <SingleSelectInput
            label={t(props.propertyKeys.quality)}
            value={props.data.properties.quality}
            errorMessage={props.getError("quality")}
            options={[
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_PHOTO_QUALITY_LOW), value: "low" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_PHOTO_QUALITY_HIGH), value: "high" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_PHOTO_QUALITY_BEST), value: "best" },
            ]}
            onSelect={(value) => props.setValue("quality", value)}
            info={{
              title: t(props.propertyKeys.quality),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.quality }),
              description: t(($) => $.FIELD_PROPERTY_PHOTO_QUALITY_DESCRIPTION),
            }}
            sameWidth
          />

          <MixedInput
            label={t(props.propertyKeys.filename)}
            info={{
              title: t(props.propertyKeys.filename),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
              description: t(($) => $.FIELD_PROPERTY_FILE_NAME_DESCRIPTION),
            }}
            value={props.data.properties.filename}
            parse={(value) =>
              parseStringTemplate(
                value,
                fieldService.getFieldsByTypes(SEARCHABLE_FIELDS),
                variableService.getVariablesByTypes(SEARCHABLE_VARIABLES),
              )
            }
            onChange={(value) => props.setValue("filename", value)}
            options={createDynamicValueOptions(t, {
              fields: fieldService.getFieldsByTypes(SEARCHABLE_FIELDS).flatMap((field) => toFieldValueOptions(field)),
              subforms: [],
              customVariables: variableService.getVariablesByTypes(SEARCHABLE_VARIABLES).map(toVariableValueOption),
              metaVariables: [],
            })}
            errorMessage={props.getError("filename")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          {!isFeatureEnabled(FeatureFlag.MULTIPLE_PHOTOS) || !props.data.properties.isMultiple ? (
            <Toggle
              block
              label={t(($) => $.REQUIRED_SWITCH)}
              checked={props.data.properties.required}
              setChecked={(checked) => props.setValue("required", checked)}
              description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
            />
          ) : (
            <>
              <NumberInput
                label={t(props.propertyKeys.min_photos)}
                value={props.data.properties.min_photos}
                required
                minValue={0}
                maxValue={MAX_PHOTOS}
                onBlur={(value) => props.setValue("min_photos", value)}
                errorMessage={props.getError("min_photos")}
              />
              <NumberInput
                label={t(props.propertyKeys.max_photos)}
                value={props.data.properties.max_photos}
                minValue={1}
                maxValue={MAX_PHOTOS}
                onBlur={(originalMaxValue) => {
                  props.setValue("max_photos", originalMaxValue);
                }}
                errorMessage={props.getError("max_photos")}
              />
              <GlobalErrorInlineMessage errors={globalErrors()} />
            </>
          )}
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(props.propertyKeys.save_to_gallery)}
            checked={props.data.properties.save_to_gallery}
            setChecked={(value) => props.setValue("save_to_gallery", value)}
            description={t(($) => $.FIELD_PROPERTY_SAVE_TO_GALLERY_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.allow_library)}
            checked={props.data.properties.allow_library}
            setChecked={(value) => props.setValue("allow_library", value)}
            description={t(($) => $.FIELD_PROPERTY_ALLOW_LIBRARY_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(PhotoField);
